import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { PhysicsTable } from "../../../entities/physics_table.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { RigidMotion } from "../../../physics/physics_protocol.js";

const UP = new THREE.Vector3( 0, 1, 0 );
const TMP_Q_DELTA = new THREE.Quaternion();
const MOVED: RAPIER.RigidBody[] = [];
const MOTION: RigidMotion = { rotate: true, planar: true, px: 0, py: 0, pz: 0, qx: 0, qy: 0, qz: 0, qw: 1, dx: 0, dy: 0, dz: 0 };

const DEG_TO_RAD = Math.PI / 180;
const RAD_TO_DEG = 180 / Math.PI;
const TWO_PI = Math.PI * 2;
const DEFAULT_ANIMATE_SPEED = 180 * DEG_TO_RAD;

export class FieldRotator
{
  private angle = 0;
  private spin_speed = 0;
  private animation: { target: number; speed: number } | null = null;

  constructor(
    private readonly table: PhysicsTable,
    private readonly get_bodies: () => Iterable<RAPIER.RigidBody>
  )
  {
  }

  public isActive(): boolean
  {
    return this.spin_speed !== 0 || this.animation !== null;
  }

  public setAngle( deg: number, animate?: { deg_per_sec?: number } ): void
  {
    this.spin_speed = 0;
    const target = deg * DEG_TO_RAD;

    if ( !animate )
    {
      this.animation = null;
      this.applyDelta( this.shortestDeltaTo( target ) );
      return;
    }

    this.animation = {
      target: this.angle + this.shortestDeltaTo( target ),
      speed: Math.abs( animate.deg_per_sec ?? 0 ) * DEG_TO_RAD || DEFAULT_ANIMATE_SPEED
    };
  }

  public startSpin( deg_per_sec: number ): void
  {
    this.animation = null;
    this.spin_speed = deg_per_sec * DEG_TO_RAD;
  }

  public stopSpin(): void
  {
    this.spin_speed = 0;
  }

  public getAngleDeg(): number
  {
    const deg = ( this.angle * RAD_TO_DEG ) % 360;

    return deg < 0 ? deg + 360 : deg;
  }

  public step( dt: number ): void
  {
    if ( this.spin_speed !== 0 )
    {
      this.applyDelta( this.spin_speed * dt );
      return;
    }

    if ( this.animation )
    {
      const remaining = this.animation.target - this.angle;
      const max_step = this.animation.speed * dt;
      const delta = THREE.MathUtils.clamp( remaining, -max_step, max_step );

      if ( Math.abs( remaining ) <= max_step )
        this.animation = null;

      this.applyDelta( delta );
    }
  }

  private shortestDeltaTo( target_rad: number ): number
  {
    let delta = ( target_rad - this.angle ) % TWO_PI;

    if ( delta > Math.PI )
      delta -= TWO_PI;
    else if ( delta < -Math.PI )
      delta += TWO_PI;

    return delta;
  }

  private applyDelta( delta: number ): void
  {
    if ( delta === 0 )
      return;

    this.angle += delta;

    TMP_Q_DELTA.setFromAxisAngle( UP, delta );
    const pivot = this.table.getPivot();

    MOVED.length = 0;
    for ( const body of this.get_bodies() )
    {
      // Entities may remove their body from the world before leaving the
      // entity list (e.g. a broken watermelon while its slices fade).
      if ( !body.isValid() )
        continue;

      if ( this.table.isOutside( body.translation() ) )
        continue;

      MOVED.push( body );
    }

    MOTION.px = pivot.x; MOTION.py = pivot.y; MOTION.pz = pivot.z;
    MOTION.qx = TMP_Q_DELTA.x; MOTION.qy = TMP_Q_DELTA.y; MOTION.qz = TMP_Q_DELTA.z; MOTION.qw = TMP_Q_DELTA.w;
    PhysicsWorld.moveBodies( MOVED, MOTION );
    MOVED.length = 0;

    this.table.setFieldAngle( this.angle );
  }
}
